import React, { useState, useEffect, useCallback } from 'react';
import VirtualizedReportTable from './VirtualizedReportTable';
import paginatedReportService, { PaginationParams } from '../../services/paginatedReportService';
import { FilterState } from '../../types/filterTypes';
import { ReportData } from '../../types/reportTypes';
import { formatCurrency, formatPercentage } from '../../utils/formatters';
import {
  ReportHeaderContainer,
  ReportSection,
  ReportHeading,
  ReportSubheading
} from './ReportResponsiveStyles';

interface PaginatedReportViewProps {
  reportId: string;
  filterState: FilterState;
  initialData?: ReportData;
}

/**
 * PaginatedReportView component for handling large datasets
 * Uses server-side pagination and virtualized rendering for optimal performance
 */
const PaginatedReportView: React.FC<PaginatedReportViewProps> = ({
  reportId,
  filterState,
  initialData
}) => {
  // KPI data is always loaded at once (small dataset)
  const [kpiData, setKpiData] = useState(initialData?.kpis);
  
  // State for paginated job data
  const [jobData, setJobData] = useState<any[]>([]);
  const [jobDataLoading, setJobDataLoading] = useState(false);
  const [jobPagination, setJobPagination] = useState<PaginationParams>({
    page: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0
  });
  
  // State for paginated sales by source data
  const [salesBySource, setSalesBySource] = useState<any[]>([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesPagination, setSalesPagination] = useState<PaginationParams>({
    page: 1,
    pageSize: 5,
    totalItems: 0,
    totalPages: 0
  });
  
  // State for paginated ad spend data
  const [adSpendData, setAdSpendData] = useState<any[]>([]);
  const [adSpendLoading, setAdSpendLoading] = useState(false);
  const [adSpendPagination, setAdSpendPagination] = useState<PaginationParams>({
    page: 1,
    pageSize: 5,
    totalItems: 0,
    totalPages: 0
  });
  
  // State for sort configurations
  const [jobSortConfig, setJobSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'contractTotal',
    direction: 'desc'
  });
  
  const [salesSortConfig, setSalesSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'totalSales',
    direction: 'desc'
  });
  
  const [adSpendSortConfig, setAdSpendSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'spend',
    direction: 'desc'
  });
  
  // Load job data with pagination
  const loadJobData = useCallback(async (page = 1) => {
    if (!reportId) return;
    
    setJobDataLoading(true);
    
    try {
      // Add sort configuration to filter state
      // Cast to any to avoid TypeScript errors with sortConfig format
      const sortedFilterState = {
        ...filterState,
        sortConfig: [{
          field: jobSortConfig.key,
          direction: jobSortConfig.direction
        }]
      } as any;
      
      const pagination = {
        ...jobPagination,
        page
      };
      
      const response = await paginatedReportService.getPaginatedJobData(
        reportId,
        sortedFilterState,
        pagination
      );
      
      setJobData(response.data);
      setJobPagination(response.pagination);
    } catch (error) {
      console.error('Error loading job data:', error);
    } finally {
      setJobDataLoading(false);
    }
  }, [reportId, filterState, jobPagination, jobSortConfig]);
  
  // Load sales data with pagination
  const loadSalesData = useCallback(async (page = 1) => {
    if (!reportId) return;
    
    setSalesLoading(true);
    
    try {
      // Add sort configuration to filter state
      // Cast to any to avoid TypeScript errors with sortConfig format
      const sortedFilterState = {
        ...filterState,
        sortConfig: [{
          field: salesSortConfig.key,
          direction: salesSortConfig.direction
        }]
      } as any;
      
      const pagination = {
        ...salesPagination,
        page
      };
      
      const response = await paginatedReportService.getPaginatedSalesBySource(
        reportId,
        sortedFilterState,
        pagination
      );
      
      setSalesBySource(response.data);
      setSalesPagination(response.pagination);
    } catch (error) {
      console.error('Error loading sales data:', error);
    } finally {
      setSalesLoading(false);
    }
  }, [reportId, filterState, salesPagination, salesSortConfig]);
  
  // Load ad spend data with pagination
  const loadAdSpendData = useCallback(async (page = 1) => {
    if (!reportId) return;
    
    setAdSpendLoading(true);
    
    try {
      // Add sort configuration to filter state
      // Cast to any to avoid TypeScript errors with sortConfig format
      const sortedFilterState = {
        ...filterState,
        sortConfig: [{
          field: adSpendSortConfig.key,
          direction: adSpendSortConfig.direction
        }]
      } as any;
      
      const pagination = {
        ...adSpendPagination,
        page
      };
      
      const response = await paginatedReportService.getPaginatedAdSpendBySource(
        reportId,
        sortedFilterState,
        pagination
      );
      
      setAdSpendData(response.data);
      setAdSpendPagination(response.pagination);
    } catch (error) {
      console.error('Error loading ad spend data:', error);
    } finally {
      setAdSpendLoading(false);
    }
  }, [reportId, filterState, adSpendPagination, adSpendSortConfig]);
  
  // Handle job data page change
  const handleJobPageChange = useCallback((page: number) => {
    loadJobData(page);
  }, [loadJobData]);
  
  // Handle sales data page change
  const handleSalesPageChange = useCallback((page: number) => {
    loadSalesData(page);
  }, [loadSalesData]);
  
  // Handle ad spend data page change
  const handleAdSpendPageChange = useCallback((page: number) => {
    loadAdSpendData(page);
  }, [loadAdSpendData]);
  
  // Handle job data sorting
  const handleJobSort = useCallback((key: string) => {
    setJobSortConfig(prevConfig => {
      // If sorting by the same column, toggle the direction
      if (prevConfig.key === key) {
        return {
          ...prevConfig,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      
      // If sorting by a new column, default to descending
      return {
        key,
        direction: 'desc'
      };
    });
  }, []);
  
  // Handle sales data sorting
  const handleSalesSort = useCallback((key: string) => {
    setSalesSortConfig(prevConfig => {
      // If sorting by the same column, toggle the direction
      if (prevConfig.key === key) {
        return {
          ...prevConfig,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      
      // If sorting by a new column, default to descending
      return {
        key,
        direction: 'desc'
      };
    });
  }, []);
  
  // Handle ad spend data sorting
  const handleAdSpendSort = useCallback((key: string) => {
    setAdSpendSortConfig(prevConfig => {
      // If sorting by the same column, toggle the direction
      if (prevConfig.key === key) {
        return {
          ...prevConfig,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      
      // If sorting by a new column, default to descending
      return {
        key,
        direction: 'desc'
      };
    });
  }, []);
  
  // Initial data load
  useEffect(() => {
    if (!reportId) return;
    
    loadJobData();
    loadSalesData();
    loadAdSpendData();
  }, [reportId, loadJobData, loadSalesData, loadAdSpendData]);
  
  // Reload data when sort config changes
  useEffect(() => {
    loadJobData(1); // Reset to first page when sort changes
  }, [jobSortConfig, loadJobData]);
  
  useEffect(() => {
    loadSalesData(1); // Reset to first page when sort changes
  }, [salesSortConfig, loadSalesData]);
  
  useEffect(() => {
    loadAdSpendData(1); // Reset to first page when sort changes
  }, [adSpendSortConfig, loadAdSpendData]);
  
  // Column definitions for job data table
  const jobColumns = [
    {
      id: 'jobInfo',
      header: 'Job Information',
      accessor: (item: any) => item.jobInfo,
      className: 'font-medium',
    },
    {
      id: 'leadSource',
      header: 'Lead Source',
      accessor: (item: any) => item.leadSource,
    },
    {
      id: 'trade',
      header: 'Trade',
      accessor: (item: any) => item.trade,
    },
    {
      id: 'contractTotal',
      header: 'Contract Total',
      accessor: (item: any) => formatCurrency(item.contractTotal),
      className: 'text-right',
    },
    {
      id: 'grossMarginPct',
      header: 'Margin %',
      accessor: (item: any) => formatPercentage(item.grossMarginPct),
      className: 'text-right',
    },
    {
      id: 'grossProfit',
      header: 'Gross Profit',
      accessor: (item: any) => formatCurrency(item.grossProfit),
      className: 'text-right',
    },
    {
      id: 'salesCommission',
      header: 'Commission',
      accessor: (item: any) => formatCurrency(item.salesCommission),
      className: 'text-right',
    },
    {
      id: 'netProfit',
      header: 'Net Profit',
      accessor: (item: any) => formatCurrency(item.netProfit),
      className: 'text-right',
    },
  ];
  
  // Column definitions for sales by source table
  const salesColumns = [
    {
      id: 'source',
      header: 'Lead Source',
      accessor: (item: any) => item.source,
      className: 'font-medium',
    },
    {
      id: 'count',
      header: 'Sales Count',
      accessor: (item: any) => item.count,
      className: 'text-right',
    },
    {
      id: 'totalSales',
      header: 'Total Sales',
      accessor: (item: any) => formatCurrency(item.totalSales),
      className: 'text-right',
    },
    {
      id: 'grossProfit',
      header: 'Gross Profit',
      accessor: (item: any) => formatCurrency(item.grossProfit),
      className: 'text-right',
    },
    {
      id: 'roi',
      header: 'ROI',
      accessor: (item: any) => item.roi?.toFixed(2) + 'x',
      className: 'text-right',
    },
    {
      id: 'costPerSale',
      header: 'Cost Per Sale',
      accessor: (item: any) => formatCurrency(item.costPerSale || 0),
      className: 'text-right',
    },
  ];
  
  // Column definitions for ad spend table
  const adSpendColumns = [
    {
      id: 'source',
      header: 'Source',
      accessor: (item: any) => item.source,
      className: 'font-medium',
    },
    {
      id: 'spend',
      header: 'Ad Spend',
      accessor: (item: any) => formatCurrency(item.spend),
      className: 'text-right',
    },
    {
      id: 'percent',
      header: 'Percentage of Total',
      accessor: (item: any) => {
        const total = adSpendData.reduce((sum, item) => sum + item.spend, 0);
        return formatPercentage(item.spend / total);
      },
      className: 'text-right',
    },
  ];
  
  return (
    <div>
      {/* KPI Overview Section */}
      {kpiData && (
        <ReportSection>
          <ReportHeading>Key Performance Metrics</ReportHeading>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-600 mb-1">Total Sales</div>
              <div className="text-2xl font-bold text-blue-800">{formatCurrency(kpiData.totalSales || 0)}</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-600 mb-1">Gross Profit</div>
              <div className="text-2xl font-bold text-green-800">{formatCurrency(kpiData.grossProfit || 0)}</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-600 mb-1">Ad Spend</div>
              <div className="text-2xl font-bold text-orange-800">{formatCurrency(kpiData.adSpend || 0)}</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-600 mb-1">Net Profit</div>
              <div className="text-2xl font-bold text-purple-800">{formatCurrency(kpiData.netProfit || 0)}</div>
            </div>
          </div>
          <div className="mt-4 text-center">
            <div className="text-lg font-semibold">Net Profit Margin: <span className="text-blue-700">{formatPercentage(kpiData.netProfitPercent || 0)}</span></div>
            <div className="text-lg font-semibold">Gross Profit Margin: <span className="text-green-700">{formatPercentage(kpiData.grossProfitPercent || 0)}</span></div>
          </div>
        </ReportSection>
      )}
      
      {/* Sales By Source Section */}
      <ReportSection>
        <ReportHeading>Sales by Lead Source</ReportHeading>
        <p className="mb-4 text-gray-600">Showing sales data grouped by lead source with ROI calculations.</p>
        
        <VirtualizedReportTable
          data={salesBySource}
          isLoading={salesLoading}
          columns={salesColumns}
          pagination={salesPagination}
          onPageChange={handleSalesPageChange}
          sortConfig={salesSortConfig}
          onSort={handleSalesSort}
          visibleRows={5}
          enableVirtualization={(salesPagination.totalItems || 0) > 10}
          emptyMessage="No sales data available for the selected filters."
        />
      </ReportSection>
      
      {/* Ad Spend Distribution */}
      <ReportSection>
        <ReportHeading>Ad Spend Distribution</ReportHeading>
        <p className="mb-4 text-gray-600">Showing advertising spend data grouped by source.</p>
        
        <VirtualizedReportTable
          data={adSpendData}
          isLoading={adSpendLoading}
          columns={adSpendColumns}
          pagination={adSpendPagination}
          onPageChange={handleAdSpendPageChange}
          sortConfig={adSpendSortConfig}
          onSort={handleAdSpendSort}
          visibleRows={5}
          enableVirtualization={(adSpendPagination.totalItems || 0) > 10}
          emptyMessage="No ad spend data available for the selected filters."
        />
      </ReportSection>
      
      {/* Individual Job Analysis */}
      <ReportSection>
        <ReportHeading>Individual Job Analysis</ReportHeading>
        <p className="mb-4 text-gray-600">Showing detailed information for individual jobs. Use the pagination controls to navigate through large datasets efficiently.</p>
        
        <VirtualizedReportTable
          data={jobData}
          isLoading={jobDataLoading}
          columns={jobColumns}
          pagination={jobPagination}
          onPageChange={handleJobPageChange}
          sortConfig={jobSortConfig}
          onSort={handleJobSort}
          visibleRows={10}
          containerHeight={500}
          enableVirtualization={(jobPagination.totalItems || 0) > 20}
          emptyMessage="No job data available for the selected filters."
        />
      </ReportSection>
    </div>
  );
};

export default PaginatedReportView;